// ChatBot.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './common/Styles.css'
import { Button, Grid, Typography } from '@mui/material';
import { CONST_BUSINESS_MAIL, formatAIResponse, mappedResponseList } from './common/Helper';
import DebugService from '../services/DebugService';
import { CircularProgress } from '@mui/material';

const Chatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([{ role: "assistant", content: "Welcome! Please enter your email to start chatting." }]);
  const [input, setInput] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (shouldScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      setShouldScroll(false);
    }
  }, [shouldScroll]);
  

  useEffect(() => {
    const storedEmail = localStorage.getItem('chatbot_email');
    if (storedEmail) {
      setEmail(storedEmail);
      setMessages([{ role: "assistant", content: "How can I assist you?" }]);
    }
  }, []);

  const sendMessage = async () => {
    if (!input) return;
  
    if (!email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; 
      if (!emailRegex.test(input)) {
        setMessages([...messages, { role: 'assistant', content: "Please enter a valid email address to continue." }]);
        setInput('');
        return;
      }
  
      setEmail(input);
      localStorage.setItem('chatbot_email', input);
      setMessages([...messages, { role: 'user', content: input }, { role: 'assistant', content: "Great! How can I assist you?" }]);
      setInput('');
      return;
    }
  
    let reply = 'Sorry, I did not understand'
  
    const alphaNumericInput = input.replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase()
    const replyObject = mappedResponseList.find(r => r['in'].includes(alphaNumericInput))
  
    if (replyObject && replyObject['reply']) {
      reply = replyObject['reply']
      setMessages([...messages, { role: 'user', content: input }, { role: 'assistant', content: reply }]);
      setShouldScroll(true)
      setInput('');
      // DebugService.log(`Found reply ${reply} from response list for input ${input}`, input)
    } else {
      
      const newMessages = [...messages, { role: 'user', content: input }];
      setMessages(newMessages);
      setShouldScroll(true)
      setLoading(true);
      setInput('');

      try {
        const response = await axios.post(
          process.env.REACT_APP_CHATBOT_API_URL, { email, message: input }
        )
        reply = formatAIResponse(response?.data?.data);

      } catch (e) {
        DebugService.error(`Chatbot component: Error caught while chatting`, e)
        reply = `We are currently unavailable here. Please connect with us at ${CONST_BUSINESS_MAIL}`;
      }
  
      setLoading(false);
      const updatedMessages = [...newMessages, { role: 'assistant', content: reply }];
      setMessages(updatedMessages);
    }
  };
  
  return (
    <div className={`chatbot ${isOpen ? 'open' : ''}`}>
      <div className="header">
        <Grid container style={{padding: '6px'}}>
          <Grid item xs={2}>
            <img
              src={`/chatbot-short.svg`}
              alt={'Chatbot Icon'}
              width={50}
              height={49}
            /></Grid>
          <Grid item xs={8}>
            <div style={{padding: '0 10px'}}>
              <Typography variant='subtitle2' style={{ textTransform: 'none' }} color={'#FFF'}>Support Chatbot</Typography>
              <Typography variant='caption' style={{ textTransform: 'none' }} color={'#FF9A00'}>Online</Typography>
            </div></Grid>
          <Grid item xs={2}>
            <button className='close' onClick={onClose}>X</button>
          </Grid>
        </Grid>
      </div>
      <div className="messages">
        {messages.map((msg, index) => (
          <div key={index} className={msg.role}>
            <Typography
              variant="caption"
              dangerouslySetInnerHTML={{ __html: msg.content }} 
            />
          </div>
        ))}
        {loading && (
          <CircularProgress size={24} sx={{ alignSelf: "left" }} />
        )}
        <div ref={bottomRef} />
      </div>
      <div className="input">
        <input
          type="text"
          placeholder={email ? "Type your message..." : "Enter your email..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyUp={(e) => {
            if (e?.key?.toLocaleLowerCase() === 'enter') {
              sendMessage()
            }
          }}
        />
        <Button className='send-btn' variant="contained" onClick={sendMessage}>
          <img
            src={`/chat-send.svg`}
            alt={'Chatbot Send'}
            width={23}
            height={21}
          />
        </Button>
      </div>
    </div>
  );
};

export default Chatbot;
